import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  AccessTime,
  PersonAddAlt,
  Add,
} from '@mui/icons-material';
import { LeaderRequest } from 'services/api/apis/organizations/organizations.api';
import { OrganizationLeader, OrganizationLeadersSection } from './OrganizationLeadersSection';

export interface MembersTabProps {
  membershipStatus: 'none' | 'pending' | 'approved';
  organizationName: string;

  // Organization leaders sub-section (CAM-684)
  orgLeaders: OrganizationLeader[];
  isLeadersExpanded: boolean;
  onToggleLeaders: () => void;
  isEligibleForLeaderRequest: boolean;
  hasPendingLeaderRequest: boolean;
  myLeaderRequest: LeaderRequest | null | undefined;
  onOpenRoleModal: () => void;
  onEditLeaderRequest: () => void;
  onCancelLeaderRequest: () => Promise<void>;
  onAcceptLeaderInvite: (acceptedTerms: boolean) => Promise<void>;
  onDeclineLeaderInvite: () => Promise<void>;
  schoolTermsUrl?: string;
  schoolTermsDisplayName?: string;

  // Membership actions (CAM-676/677/678)
  onRequestMembership: () => Promise<void>;
  onCancelMembershipRequest: () => Promise<void>;
  onLeaveOrganization: () => Promise<void>;

  /** True when the organization belongs to the current user's school. */
  isSameSchool: boolean;

  // CAM-684
  isAcceptedLeader: boolean;
  onLeaveMyRole: () => Promise<void>;
}

const MEMBER_SUBTITLE = 'Get access to members-only events from this organization.';

/**
 * Compact inline button used next to the status badge in the Pending and
 * Membership Confirmed states. Matches the Figma "Master | Button | Secondary |
 * Large" spec (auto-width, 47px tall, outlined).
 */
const InlineStatusButton = ({
  label,
  onClick,
  loading,
  variant = 'primary',
  minWidth = 150,
}: {
  label: string;
  onClick: () => void;
  loading?: boolean;
  variant?: 'primary' | 'danger';
  minWidth?: number;
}): ReactElement => {
  const color = variant === 'danger' ? '#9B0000' : '#23305F';
  return (
    <Button
      variant="outlined"
      disabled={loading}
      onClick={onClick}
      sx={{
        height: 47,
        minWidth,
        px: 16,
        whiteSpace: 'nowrap',
        textTransform: 'none',
        fontFamily: 'Poppins',
        fontWeight: 500,
        fontSize: pxToEm(14),
        lineHeight: 1.5,
        borderRadius: '6px',
        boxShadow: 'none',
        borderColor: color,
        color,
        '&:hover': {
          borderColor: color,
          backgroundColor:
            variant === 'danger' ? 'rgba(155,0,0,0.04)' : 'rgba(35,48,95,0.04)',
        },
      }}
    >
      {label}
    </Button>
  );
};

/**
 * Status row — icon + uppercase status label and an inline button. Layout
 * differs between states per the Figma design:
 *   - "pending":  group is centered in the row with a 16px gap between badge
 *                 and button (Frame 5169 in Figma is only 309px wide and
 *                 horizontally centered in the 536px container).
 *   - "approved": badge pinned left, button pinned right on desktop
 *                 (Frame 5166 in Figma spans the full 536px container).
 *                 On mobile the badge and button stack vertically and
 *                 center — see mobile Figma frame 3737-3861.
 */
const StatusRow = ({
  icon,
  label,
  action,
  align,
}: {
  icon: ReactElement;
  label: string;
  action: ReactElement;
  align: 'center' | 'space-between';
}): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexDirection: align === 'space-between' ? { xs: 'column', sm: 'row' } : 'row',
      justifyContent: align === 'space-between' ? { xs: 'center', sm: 'space-between' } : align,
      gap: align === 'space-between' ? { xs: 16, sm: 16 } : 16,
      py: 4,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, color: '#383838' }}>
      {icon}
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontWeight: 500,
          fontSize: pxToEm(12),
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#383838',
        }}
      >
        {label}
      </Typography>
    </Box>
    {action}
  </Box>
);

export const MembersTab = ({
  membershipStatus,
  organizationName,
  orgLeaders,
  isLeadersExpanded,
  onToggleLeaders,
  isEligibleForLeaderRequest,
  hasPendingLeaderRequest,
  myLeaderRequest,
  onOpenRoleModal,
  onEditLeaderRequest,
  onCancelLeaderRequest,
  onAcceptLeaderInvite,
  onDeclineLeaderInvite,
  schoolTermsUrl,
  schoolTermsDisplayName,
  onRequestMembership,
  onCancelMembershipRequest,
  onLeaveOrganization,
  isSameSchool,
  isAcceptedLeader,
  onLeaveMyRole,
}: MembersTabProps): ReactElement => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const withBusy = (fn: () => Promise<void>) => async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await fn();
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStateHeader = (): ReactElement | null => {
    if (!isSameSchool) return null;

    if (membershipStatus === 'approved') {
      return (
        <StatusRow
          align="space-between"
          icon={<CheckCircleOutline sx={{ fontSize: 20 }} />}
          label="Membership Confirmed"
          action={
            <InlineStatusButton
              variant="danger"
              label="Leave Organization"
              onClick={withBusy(onLeaveOrganization)}
              loading={isSubmitting}
              minWidth={167}
            />
          }
        />
      );
    }

    if (membershipStatus === 'pending') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          <Box sx={{ textAlign: 'center', px: 8 }}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: pxToEm(14),
                color: '#383838',
              }}
            >
              Become a general member
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: pxToEm(14),
                color: '#6B6B6B',
                mt: 2,
              }}
            >
              {MEMBER_SUBTITLE}
            </Typography>
          </Box>
          <StatusRow
            align="center"
            icon={<AccessTime sx={{ fontSize: 20 }} />}
            label="Pending Approval"
            action={
              <InlineStatusButton
                label="Cancel"
                onClick={withBusy(onCancelMembershipRequest)}
                loading={isSubmitting}
              />
            }
          />
        </Box>
      );
    }

    // Default (CAM-676) — compact lavender/gray banner with a "+" icon button.
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          p: 16,
          borderRadius: '8px',
          backgroundColor: '#EFF0F6',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
          <PersonAddAlt sx={{ fontSize: 22, color: '#23305F', mt: '2px' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: pxToEm(14),
                color: '#383838',
              }}
            >
              Become a general member
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 400,
                fontSize: pxToEm(13),
                color: '#6B6B6B',
                mt: 2,
              }}
            >
              {MEMBER_SUBTITLE}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={withBusy(onRequestMembership)}
          disabled={isSubmitting}
          aria-label={`Join ${organizationName}`}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '8px',
            border: '1px solid #23305F',
            color: '#23305F',
            backgroundColor: '#FFFFFF',
            '&:hover': { backgroundColor: '#F8F8F8', borderColor: '#23305F' },
            '&.Mui-disabled': { borderColor: '#BDBDBD', color: '#BDBDBD' },
          }}
        >
          <Add sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>
    );
  };

  // The Organization Leaders section is orthogonal to general membership —
  // leaders and members are separate roles, so a pending member request
  // must not hide the leaders list. Users with a pending membership still
  // need to see who leads the org (and, if eligible, request leader access
  // or manage their own leader invite/role).
  const shouldShowLeadersSection =
    isSameSchool &&
    (orgLeaders.length > 0 ||
      isEligibleForLeaderRequest ||
      hasPendingLeaderRequest ||
      isAcceptedLeader);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {renderStateHeader()}

      {shouldShowLeadersSection && (
        <OrganizationLeadersSection
          orgLeaders={orgLeaders}
          isExpanded={isLeadersExpanded}
          onToggle={onToggleLeaders}
          isEligibleForLeaderRequest={isEligibleForLeaderRequest}
          hasPendingRequest={hasPendingLeaderRequest}
          myLeaderRequest={myLeaderRequest}
          organizationName={organizationName}
          onOpenRoleModal={onOpenRoleModal}
          onEditRequest={onEditLeaderRequest}
          onCancelRequest={onCancelLeaderRequest}
          onAcceptInvite={onAcceptLeaderInvite}
          onDeclineInvite={onDeclineLeaderInvite}
          schoolTermsUrl={schoolTermsUrl}
          schoolTermsDisplayName={schoolTermsDisplayName}
          isAcceptedLeader={isAcceptedLeader}
          onLeaveMyRole={onLeaveMyRole}
        />
      )}
    </Box>
  );
};
